import { ControlListProps, ControlListConfirmationDialogProps, useTranslations, useFormData, useRuntimeEnv, useTranslationLang, useUrlParamsValue } from "@bsm/component";
import { useMemo, useState, useEffect } from "react";
import { AppointmentVacationBalance } from "../../vacation/components/AppointmentVacationBalance";
import { useTimeOffTypeList } from "../../../hooks/useTimeOffTypeList";
import { useContractManagerQuery } from "hooks/useContractManagerQuery";
import _ from "lodash";
import moment from "moment";
import { TTimeOff } from "@bsm/graphql";
import { useDateIncludeWorkingDays } from "hooks/useDateIncludeWorkingDays";
import { useDateDifferenceExcludingWeekends } from "hooks/useDateDifferenceExcludingWeekends";
import { useDailyQuotaLeaveTypes } from "hooks/useDailyQuotaLeaveTypes";
import { useWorkingDayListQuery } from "../../../../calendar/hooks/useWorkingDayListQuery";
import { WorkingDayCheckControl } from "../components/WorkingDayCheckControl";
import { useWorkingDaysInPeriod } from "./useWorkingDaysInPeriod";

export const useTimeOffControlForm = (id?: number, type?: string): ControlListProps => {
    const [
        DATE_BEGIN,
        DATE_END,
        HOUR_BEGIN,
        HOUR_END,
        SAVE,
        CANCEL,
        GUEST,
        TITLE,
        ATTACHMENT,
        REASON,
        BOARD,
        LABELS,
        REPETITION,
        NOTIFY_BEFORE,
        TYPE_LABEL,
        TYPE_LEAVE,
        TYPE_ABSENCE,
        TYPE_SICK_LEAVE,
        TYPE_PERMISSION,
        APPROVE_LABEL,
        BALANCE,
        EMPLOYEE,
        VALIDATE,
        END_DATE_ERROR,
        INSUFICIENT_LEAVE_BALANCE,
        START_DATE_ERROR,
        INSUFICIENT_QUOTA,
        INCLUDE_WORKING_DAYS_ERROR,
        REMAINING_QUOTA,
        CONFIRMATION_TITLE,
        CONFIRMATION_MESSAGE,
        CONFIRMATION,
        WORKING_DAYS_DIALOG_MESSAGE,
        INCLUDE_WORKING_DAYS_LABEL,
        HOUR_END_SAME_ERROR,
        TYPE_NOT_ALLOWED_ERROR,
        IS_CRITICAL_DAY
    ] = useTranslations(i18n);

    
    const urlParams = useUrlParamsValue()
    const { formData } = useFormData<Partial<TTimeOff>>({ id: TIMEOFF_FORM_ADD_ID, formData: {}});
    const includeWorkingDays = useDateIncludeWorkingDays(formData?.employee?.id)
    const getDateDifferenceExcludingWeekends = useDateDifferenceExcludingWeekends()
    const typeAll = urlParams?.type === 'all'
    
    const isEmployeePreselected = !!urlParams?.employeeFilterCalendar && urlParams?.employeeFilterCalendar !== -1 
    const timeOffTypes = useTimeOffTypeList(!typeAll ? 'LEAVE' : undefined)
    const { runtimeEnv } = useRuntimeEnv(true)
    const workingHourBegin = runtimeEnv?.organisation?.profile?.workHourBegin ?? '06:00'
    const workingHourEnd = runtimeEnv?.organisation?.profile?.workHourEnd ?? '17:00'   
    const workRestHourEnd = runtimeEnv?.organisation?.profile?.workRestHourEnd ?? '13:00'


    const { data: contractData } = useContractManagerQuery(formData?.employee?.id, formData?.type as any);
    const dailyQuotaTypes = useDailyQuotaLeaveTypes()
    const currentYear = moment(formData?.dateBegin).year() || moment().year()
    const { data: workingDaysData } = useWorkingDayListQuery(false, currentYear, formData?.employee?.id)
    const getWorkingDaysInPeriod = useWorkingDaysInPeriod();

    const [soldeLabel, setSoldeLabel] = useState(BALANCE);

    useEffect(() => {
        if (formData?.type && contractData?.leaveBalance) {
            const typeObj = timeOffTypes?.find(t => t.value === formData?.type as any);
            const designation = typeObj?.value as string;
            
            if (dailyQuotaTypes.includes(designation)) {
              setSoldeLabel(REMAINING_QUOTA);
            } else {
              setSoldeLabel(BALANCE);
            }
        }
    }, [timeOffTypes, BALANCE]);

    const form = useMemo(() => [
      {
        type: "hidden",
        name: "id",
      },
      [
        {
          type: "select",
          name: "employee",
          label: EMPLOYEE,
          required: true,
          valueObject: true,
          valueProperty: "id",
          ...(isEmployeePreselected ? {
            readonly: true,
            value: { id: urlParams.employeeFilterCalendar }
          } : {}),
          GQLProps: {
            gql: `
                    query {
                        contracts (filter:{data:{user:{department:{id:${(runtimeEnv?.user as any)?.department?.id ?? null}}}},sort: [{ sort: "ASC", value: "user.fullName" }] }) {
                            data{
                                user {
                                    id fullName 
                                    profilePicture { id }
                                    department { id designation }
                                }
                            }
                        }
                    }
            `,
            rootProp: "contracts.data",
            labelProp: "user.fullName",
            valueProp: "user.id",
            userIdProp: "user.id",
          },
        },
        {
          type: "solde",
          name: "solde",
          label: soldeLabel,
        },
      ],
      ...(!window.location.pathname.includes("edit")
        ? [
            {
              type: "select",
              name: "type",
              label: TYPE_LABEL,
              required: true,
              options: timeOffTypes,
            },
          ]
        : []),
      [
        {
          type: "date-picker",
          name: "dateBegin",
          label: DATE_BEGIN,
          required: true,
          autoComplete: "off",
        },
        {
          type: "hour",
          name: "hourBegin",
          interval: 60,
          minHour: workingHourBegin,
          maxHour: workingHourEnd,
          value: workingHourBegin,
          label: HOUR_BEGIN,
        },
      ],
      [
        {
          type: "date-picker",
          name: "dateEnd",
          label: DATE_END,
          required: true,
          autoComplete: "off",
        },
        {
          type: "hour",
          name: "hourEnd",
          options: [{ label: workingHourBegin, value: workingHourBegin }, { label: workRestHourEnd, value: workRestHourEnd }],
          value: workingHourBegin,
          label: HOUR_END,
        },
      ],
      {
        type: "textarea",
        name: "reason",
        label: REASON,
        required: true,
      },
      [
        {
          type: "lov-select",
          multiple: true,
          name: "labels",
          label: LABELS,
          tp: "APPOINTMENT_LABELS",
        },
        {
          type: "file",
          name: "files",
          label: ATTACHMENT,
          multiple: true,
          required: !["LEAVE", "ABSENCE"]?.includes(type),
        },
      ],
    ], [soldeLabel, timeOffTypes, EMPLOYEE, TYPE_LABEL, DATE_BEGIN, HOUR_BEGIN, DATE_END, HOUR_END, REASON, LABELS, ATTACHMENT, type, workingHourBegin, workingHourEnd, workRestHourEnd, runtimeEnv, isEmployeePreselected, urlParams?.employeeFilterCalendar]);

    const handleValidate = (name: string, value: any, formData_: any) => {
        
        if(name === "dateBegin") {
            const { isWorkingDay, isCriticalDay } = includeWorkingDays(moment(value))
            if (isCriticalDay) {
              const enabledType = ["PERMISSION", "ABSENCE", "SICK_LEAVE"]
              if(!enabledType.includes(formData_?.type)) {
                return IS_CRITICAL_DAY
              }
            }
            if (!isWorkingDay) {
              return INCLUDE_WORKING_DAYS_ERROR;
            }
            const dateBeginIsAfterDateEnd = moment(formData_?.dateBegin, 'YYYY-MM-DD')?.isAfter(moment(formData_?.dateEnd, 'YYYY-MM-DD'))
            if(formData_?.dateBegin && dateBeginIsAfterDateEnd) {
                return START_DATE_ERROR
            }
        }
        if(name === "dateEnd") {
            const { isWorkingDay, isCriticalDay } = includeWorkingDays(moment(value))
            if (isCriticalDay) {
              const enabledType = ["PERMISSION", "ABSENCE", "SICK_LEAVE"]
              if(!enabledType.includes(formData_?.type)) {
                return IS_CRITICAL_DAY
              }
            }

            if (!isWorkingDay) {
              return INCLUDE_WORKING_DAYS_ERROR;
            }

            const endDateIsBefore = moment(formData_?.dateEnd, 'YYYY-MM-DD').isBefore(moment(formData_?.dateBegin, 'YYYY-MM-DD'))
            if(formData_?.dateBegin && endDateIsBefore) {
                return END_DATE_ERROR
            }

            const typeObj = timeOffTypes?.find(t => t.value === formData_?.type);
            const designation = typeObj?.value as string;

            if (designation && dailyQuotaTypes.includes(designation)) {
                const start = moment(formData_?.dateBegin);
                const end = moment(formData_?.dateEnd);
                const amount = getDateDifferenceExcludingWeekends(
                    start, 
                    end, 
                    formData_?.hourBegin, 
                    formData_?.hourEnd
                );

                const remainingQuota = (contractData?.leaveBalance as any)?.getRemainingQuota ?? 0;
                if (amount > remainingQuota) {
                    return INSUFICIENT_QUOTA
                }
            }
        }

        if(name === "hourEnd") {
            const datesIsSame = moment(formData_?.dateEnd, 'YYYY-MM-DD').isSame(moment(formData_?.dateBegin, 'YYYY-MM-DD'))
            
            if (datesIsSame && formData_?.hourBegin === formData_?.hourEnd) {
                return HOUR_END_SAME_ERROR
            }
        }
    };

    return {
        data: {
            forms: form,
            fields: {
                "solde": AppointmentVacationBalance
            },
            value: {},
            next: {
                label: SAVE,
                className: "btn-primary ",
            },
            back: {
                label: CANCEL,
            },
            graphQL: {
                entity: "timeoff",
                entityId: id,
            },
        },
        validate: handleValidate,
        onBeforeSubmitDialog: (value) => {
          const dialogs: ControlListConfirmationDialogProps[] = [];

          const typeObj = timeOffTypes?.find(t => t.value === value?.type);
          const designation = typeObj?.value as string;

          if (!isNaN(Number(value?.solde)) && !dailyQuotaTypes.includes(designation)) {
              const requestedDays = getDateDifferenceExcludingWeekends(
                  moment(value?.dateBegin), moment(value?.dateEnd),
                  value?.hourBegin, value?.hourEnd
              );
              if (requestedDays > Number(value?.solde)) {
                  dialogs.push({
                      onClose: () => {},
                      onConfirm: () => {},
                      onCancel: () => {},
                      title: CONFIRMATION_TITLE,
                      message: CONFIRMATION_MESSAGE,
                  });
              }
          }

          if (workingDaysData?.data && formData?.dateBegin && formData?.dateEnd) {
              const workingDaysInPeriod = getWorkingDaysInPeriod(
                  workingDaysData?.data,
                  formData.dateBegin,
                  formData.dateEnd
              );

              if (workingDaysInPeriod.length > 0) {
                  dialogs.push({
                      onClose: () => {},
                      onConfirm: () => {},
                      onCancel: () => {},
                      title: CONFIRMATION,
                      confirmationForm: {
                          forms: [{
                            type: "working_days_check",
                            name: "accountableWorkingDays",
                            workingDays: workingDaysInPeriod
                          }],
                          fields: { "working_days_check": WorkingDayCheckControl }
                      }
                  });
              }
          }

          return dialogs.length > 0 ? dialogs : null;
      },
        onBeforeSaving: (value) => {
            const type = timeOffTypes?.find(type => type.value === value?.type)
            let value_ = _.clone(value)

            if (!value?.id) {
                value_.type = { id: type.id }
            }

            value_.hourBegin = value?.hourBegin ? value?.hourBegin : runtimeEnv?.organisation?.profile?.workHourBegin ?? '06:00'
            value_.hourEnd = value?.hourEnd ? value?.hourEnd : runtimeEnv?.organisation?.profile?.workHourBegin ?? '06:00'
            
            if (value?.solde) {
                const start = moment(value?.dateBegin);
                const end = moment(value?.dateEnd);
                const totalDays = end.diff(start, 'days') + 1;
                value_.solde = totalDays;
            }

            if (value?.confirmationData?.accountableWorkingDays) {
              value_.accountableWorkingDays = JSON.stringify(value?.confirmationData?.accountableWorkingDays);
            }
            
            if(!id) {
                value_.status = 'new'
            }
            return value_;
           
        },
        id: TIMEOFF_FORM_ADD_ID
    };
}

const TIMEOFF_FORM_ADD_ID = "timeoff-add-leaving-form"

const i18n = [
    "BSMRH.Calendar.TimeOff.Create.Form.Label.DateAppointmentBegin",
    "BSMRH.Calendar.TimeOff.Create.Form.Label.DateAppointmentEnd",
    "BSMRH.Calendar.TimeOff.Create.Form.Label.HourAppointmentBegin",
    "BSMRH.Calendar.TimeOff.Create.Form.Label.HourAppointmentEnd",
    "std_save",
    "std_cancel",
    "Harea.Vacation.Calendar.Event.Create.Vacation.Form.Label.Guest",
    "std_title",
    "std_justification",
    "std_reason",
    "std_board",
    "std_labels",
    "Harea.Vacation.Calendar.Event.Create.Vacation.Form.Label.Repetition",
    "Harea.Vacation.Calendar.Event.Create.Vacation.Form.Label.NotifyBefore",
    "std_type",
    "std_leave",
    "std_absence",
    "std_sick_leave",
    "std_permission",
    "std_approve",
    "std_remaining_balance",
    "std_employee",
    "std_validate",
    "BSMRH.Calendar.TimeOff.Create.Form.EndDate.Error.Message",
    "BSMRH.Calendar.TimeOff.Create.Form.InsufficientBalance.Error.Message",
    "BSMRH.Calendar.TimeOff.Create.Form.StartDate.Error.Message",
    "BSMRH.Calendar.TimeOff.Create.Form.InsufficientQuota.Error.Message",
    "BSMRH.Calendar.TimeOff.Create.Form.IncludeWorkingDays.Error.Message",
    "std_remaining_quota",
    "std_confirmation",
    "BSMRH.Calendar.TimeOff.Create.Form.InsufficientBalance.Message",
    "std_confirmation",
    "BSMRH.Calendar.TimeOff.Create.WorkingDays.Dialog.Message",
    "BSMRH.Calendar.TimeOff.Create.WorkingDays.Include.Label",
    "BSMRH.Calendar.TimeOff.Create.Form.HourEnd.Same.Error",
    "BSMRH.Calendar.TimeOff.Create.Form.TypeNotAllowed.Error.Message",
    "BSMRH.Calendar.TimeOff.Create.Form.IsCriticalDay.Error.Message",
];